/** @jsxImportSource @emotion/react */

import { useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { FieldArray } from 'react-final-form-arrays'
import Button from '@mui/material/Button'
import { useMutation, useQuery } from '@apollo/client'
import * as commonClasses from 'pagesNew/Video/commonStyles'
import * as practiceTestsClasses from 'pagesNew/PracticeTests/Form/styles'
import * as detailsBlockClasses from 'components/DetailsBlock/styles'
import { TestResult, TEST_RESULTS_TAB, getTestResults, PracticeTest } from 'pagesNew/PracticeTests/Form'
import { TabsItem } from 'types/tabsItem'
import TabsNavigation from 'components/TabsNavigation'
import { validateRequired } from 'utils/validation'
import TestResultsFieldArray from 'pagesNew/PracticeTests/Form/TestResultsFieldArray'
import Revisions, { Revision } from 'components/Revisions'
import { PRACTICE_TEST_MEDIA_PATH_S3 } from 'constants/images'
import { prepareEditorHtml } from 'components/Editor'
import { PTResultWithoutHistory, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import { useQuestionsSubmitError } from 'hooks/useQuestionsSubmitError'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { BULK_UPDATE_PRACTICE_TEST_RESULTS, FIND_ALL_PRACTICE_TEST_RESULT } from 'queries/practiceTest'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import {
  findAllPracticeTestResult,
  findAllPracticeTestResultVariables,
} from '__generatedTypes__/findAllPracticeTestResult'
import Loader from 'components/Loader'
import {
  bulkUpdatePracticeTestResult,
  bulkUpdatePracticeTestResultVariables,
} from '__generatedTypes__/bulkUpdatePracticeTestResult'
import { useHistory } from 'hooks/useHistory'
import { toast } from 'utils/toast'
import * as classes from './styles'

export type FormValues = {
  history: Revision[]
  testResult: TestResult[]
}

export const prepareForSubmit = async (
  values: Partial<FormValues>,
  platformId: string
): Promise<PTResultWithoutHistory[]> => {
  const formattedTestResults = await Promise.all(
    values.testResult?.map(async (testResult) => {
      const resultTextAsHtml = await prepareEditorHtml(testResult.resultTextAsHtml ?? '', PRACTICE_TEST_MEDIA_PATH_S3)

      return {
        id: testResult.id,
        DATScore:
          typeof testResult.DATScore === 'object' ? +(testResult.DATScore?.id ?? 0) : +(testResult.DATScore ?? 0),
        resultPercantage: +(testResult.resultPercantage ?? 100),
        resultTextAsHtml,
        platform: {
          id: platformId,
        },
      }
    }) ?? []
  )

  return formattedTestResults
}

const TABS = [TEST_RESULTS_TAB]

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

type Props = {
  platform: Platform
}

export const CreateTest = withPlatformCheck(({ platform }: Props) => {
  const platformSlug = platform.slug
  const [activeTab, setActiveTab] = useState<TabsItem>(TEST_RESULTS_TAB)
  const { loadingHistory, populateHistoryWithUser } = useHistory()

  const { data, loading, refetch } = useQuery<findAllPracticeTestResult, findAllPracticeTestResultVariables>(
    FIND_ALL_PRACTICE_TEST_RESULT,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            platform: {
              id: platform.id,
            },
          },
        },
      },
    }
  )

  const [updateTestResults] = useMutation<bulkUpdatePracticeTestResult, bulkUpdatePracticeTestResultVariables>(
    BULK_UPDATE_PRACTICE_TEST_RESULTS
  )

  const { submitQuestionError, setSubmitQuestionError, handleServerError } = useQuestionsSubmitError()

  const initialValues = useMemo(() => {
    if (!data?.findAllPracticeTestResult) return { history: [], testResult: [] }

    return {
      history: (data.findAllPracticeTestResult.items[0]?.history || []) as Revision[],
      testResult: getTestResults(
        PracticeTestTypeEnum.CreatedTest,
        {
          testResult: data.findAllPracticeTestResult.items,
        } as unknown as PracticeTest,
        platformSlug,
        true
      ),
    }
  }, [data?.findAllPracticeTestResult])

  const handleSubmit = async (values: FormValues) => {
    try {
      setSubmitQuestionError(null)
      const practiceTestResultsData = await prepareForSubmit(values, platform.id)

      await updateTestResults({
        variables: {
          bulkUpdatePracticeTestResultInput: {
            practiceTestResult: practiceTestResultsData,
            history: populateHistoryWithUser(values.history),
          },
        },
      })

      await refetch()
      toast.success('Test updated successfully')
    } catch (error) {
      toast.error('Failed to update test')
      handleServerError(error)
    }
  }

  return (
    <section css={commonClasses.container}>
      <Typography variant="title3" css={commonClasses.title}>
        Custom Test Graduation
      </Typography>
      {loading || loadingHistory ? (
        <Loader />
      ) : (
        <FinalForm<FormValues>
          mutators={{ ...arrayMutators }}
          decorators={[focusOnErrors]}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, initialValues, submitting: formSubmitting }) => (
            <form onSubmit={handleSubmit} css={commonClasses.contentContainer}>
              <div css={commonClasses.block}>
                <TabsNavigation tabs={TABS} value={activeTab} onChange={setActiveTab} />

                <div css={practiceTestsClasses.tabContent(activeTab.id === TEST_RESULTS_TAB.id)}>
                  <FieldArray name="testResult" validate={validateRequired}>
                    {({ fields, meta }) => (
                      <TestResultsFieldArray fields={fields} meta={meta} passFailStandardizedScore />
                    )}
                  </FieldArray>
                </div>

                {!!initialValues.history?.length && (
                  <Revisions revisions={(initialValues?.history ?? []) as Revision[]} />
                )}
              </div>
              <div css={[detailsBlockClasses.containerBlock, classes.detailsBlock]}>
                <Typography variant="body2" css={detailsBlockClasses.title}>
                  Update
                </Typography>
                <Button color="blue" size="medium" fullWidth type="submit" disabled={formSubmitting}>
                  Update
                </Button>
                {submitQuestionError && (
                  <Typography variant="body2" css={detailsBlockClasses.errorText}>
                    {submitQuestionError}
                  </Typography>
                )}
              </div>
            </form>
          )}
        />
      )}
    </section>
  )
})
